import React, { useState, useEffect, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as MetricActions from "../actions/Actions.js";
import YoutubeModal from "../components/YoutubeModal.jsx";
import DealRow from "../components/DealRow.js";
import RatingCircle from "../components/RatingCircle.jsx";
import SizeScale from "../components/SizeScale.jsx";
import StyleModal from "../components/StyleModal.js";
import QuoteBox from "../components/QuoteBox.jsx";
import { Helmet } from "react-helmet";
import Helper from "../functions/Helper.js";
import { HashLink as Link } from "react-router-hash-link";
import {
  FacebookShareButton,
  TwitterShareButton,
  PinterestShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  EmailIcon,
  PinterestIcon,
} from "react-share";

import { Swiper, SwiperSlide } from "swiper/react";
import produce from "immer";
import { Waypoint } from "react-waypoint";
import StyleSliderItem from "../components/StyleSliderItem.js";
import Spinner from "react-bootstrap/Spinner";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";
import Gallery from "../components/Gallery.js";
import Button from "react-bootstrap/Button";
import { useShoe } from "../hooks/useShoe.js";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ExpertReviewPopup from "../components/ExpertReviewPopup.js";
import UserReviewPopup from "../components/UserReviewPopup.js";
import SimilarComparison from "../components/SimilarComparison.js";
import QuizAdd from "../components/QuizAdd.js";
import useShowSSRVersion from "../hooks/useShowSSRVersion.js";

const mergeHistos = (a, b) => {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return a.map((x, index) => ({
    ...x,
    count: a[index].count + b[index].count,
  }));
};

const getHistoCount = (histo) => {
  if (!histo) {
    return 0;
  }
  return histo.reduce(
    (previousValue, currentValue) => previousValue + currentValue.count,
    0
  );
};

const SmallHeader = styled.h2({
  "@media (min-width:992px)": {
    marginTop: 40,
  },
  fontWeight: 600,
  marginBottom: 5,
  marginTop: 50,
  fontSize: 20,
});

const activeSwitchStyle = {
  cursor: "pointer",
  flex: 1,
  padding: "4px 8px",
  textAlign: "center",
  backgroundColor: "#444",
  color: "white",
};
const inactiveSwitchStyle = {
  cursor: "pointer",
  flex: 1,
  padding: "4px 8px",
  textAlign: "center",
};

const SectionHeader = styled.h2({
  fontWeight: 700,
  marginBottom: 20,
  marginTop: 0,
  fontSize: 18,
  color: "#1a1a1a",
});

const showShopsAmount = 3;

// Category descriptions for the lorem ipsum placeholders
const categoryDescriptions = {
  traction:
    "Grip performance on various court surfaces, including dusty and clean conditions.",
  cushion:
    "Impact protection and energy return during jumps, landings, and quick movements.",
  materials:
    "Quality and durability of upper materials, breathability, and overall construction.",
  support:
    "Lateral stability, ankle support, and lockdown during aggressive cuts and movements.",
  fit: "How the shoe conforms to foot shape, break-in period, and overall comfort.",
  outdoor:
    "Durability and performance when used on outdoor courts and rough surfaces.",
  durability: "How well the shoe holds up over time with regular use and wear.",
};

function getHelmet(shoe) {
  if (shoe) {
    return (
      <Helmet>
        <title>
          {shoe.shoe_name +
            " - Review, Deals" +
            (shoe.discount < 0.95 * shoe.price
              ? " ($" + Number(shoe.discount).toFixed(0) + ")"
              : "") +
            ", Pics of " +
            (shoe.style_amount > 3 ? shoe.style_amount : "all") +
            " Colorways"}
        </title>
        <meta property="og:image:width" content="1170" />
        <meta property="og:image:height" content="658" />
        <meta
          property="og:image"
          content={"https://www.thehoopsgeek.com" + shoe.pictures[1].url}
        />
        <meta
          property="og:title"
          content={shoe.short_summary + " " + shoe.best_text}
        />
        <meta
          name="description"
          content={shoe.short_summary + " " + shoe.best_text}
        />
        <script type="application/ld+json">
          {JSON.stringify(getJsonLD(shoe))}
        </script>
        <link
          rel="preload"
          as="image"
          href={Helper.add_options_img(shoe.pictures[1].master, 585, "webp")}
          imagesrcset={Helper.create_source_set(
            { master: shoe.pictures[1].master, thumbnails: "1" },
            undefined,
            "webp"
          )}
          imagesizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
        />
      </Helmet>
    );
  }
}

function getJsonLD(shoe) {
  return {
    "@context": "http://schema.org",
    "@type": "Product",
    name: shoe.shoe_name,
    image:
      shoe.hasOwnProperty("pictures") &&
      "https://www.thehoopsgeek.com/" + shoe.pictures[1].url,
    description: shoe.short_summary,
    brand: shoe.brand,
    ...(shoe.rating !== "NA"
      ? {
        aggregateRating: {
          "@type": "AggregateRating",
          bestRating: "10",
          worstRating: "1",
          ratingValue: shoe.rating,
          ratingCount: shoe.review_amount,
        },
      }
      : {}),
    ...Helper.getOffers(shoe, shoe.colorways),
  };
}

// Category Rating Card Component with circle rating
const CategoryRatingCard = ({
  category,
  rating,
  userRating,
  description,
  summary,
  onClick,
}) => {
  const displayRating = rating !== "NA" ? rating : userRating;
  const isOutdoor = category === "outdoor";

  // For outdoor, convert to percentage (0-100)
  const outdoorPercent =
    isOutdoor && displayRating !== "NA" ? Math.round(displayRating) : null;

  // Use shoe-specific summary from API if available, otherwise keep empty
  const displayDescription = summary || "";

  return (
    <div
      onClick={onClick}
      css={{
        padding: 16,
        backgroundColor: "#fafafa",
        borderRadius: 12,
        textAlign: "center",
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
      }}
    >
      <div style={{ marginBottom: 10 }}>
        {isOutdoor ? (
          // Outdoor uses percentage display
          <div
            style={{
              width: 85,
              height: 85,
              margin: "0 auto",
              borderRadius: "50%",
              backgroundColor: "#fff",
              border: `6px solid ${outdoorPercent !== null
                ? Helper.get_color(displayRating, "outdoor")
                : "#eee"
                }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 26, fontWeight: 600, color: "#666" }}>
              {outdoorPercent !== null ? `${outdoorPercent}%` : "NA"}
            </span>
          </div>
        ) : (
          <RatingCircle
            containerStyle={{
              width: 85,
              height: 85,
              margin: "0 auto",
            }}
            rating={displayRating}
            textStyle={{ fontSize: 34, lineHeight: "34px" }}
          />
        )}
      </div>
      <div
        style={{
          fontWeight: 700,
          fontSize: 15,
          marginBottom: 8,
          color: "#2d3748",
          textTransform: "uppercase",
        }}
      >
        {Helper.capitalize(category)}
      </div>
      <div
        style={{
          fontSize: 14,
          color: "#333",
          lineHeight: 1.6,
        }}
      >
        {displayDescription}
      </div>
    </div>
  );
};

const CategoryBlock = (props) => {
  let [option, setOption] = useState(0);
  let { category, memoShoe, reviews, showYoutube, shoeCount } = props;

  let histo = (passedOption) => {
    if (passedOption === 0) {
      return (
        mergeHistos(
          memoShoe.expert_histo[category],
          memoShoe.user_histo[category]
        ) ?? []
      );
    } else if (passedOption === 1) {
      return memoShoe.expert_histo[category] ?? [];
    } else if (passedOption === 2) {
      return memoShoe.user_histo[category] ?? [];
    }
  };

  return (
    <div key={category}>
      <h3
        className="thg_h3_size"
        id={Helper.capitalize(category)}
        style={{ marginBottom: 10, marginTop: 100 }}
      >
        {Helper.capitalize(category)} of the {memoShoe.shoe_name}:
      </h3>
      <div>
        {!["width", "size"].includes(category) && (
          <div style={{ marginBottom: 15 }}>
            {category !== "durability" && (
              <div style={{ fontSize: 16 }}>
                Expert {Helper.capitalize(category)} Rating:&nbsp;
                <span
                  style={{
                    fontWeight: "700",
                    fontSize: 18,
                    color: Helper.get_color(memoShoe[category], "text"),
                  }}
                >
                  {Helper.printRating(memoShoe[category])}
                </span>
                &nbsp;
                {memoShoe[category] !== "NA" && (
                  <span style={{}}>
                    (ranks{" "}
                    <Link
                      style={{ fontWeight: "600" }}
                      to={Helper.getSortUrl(category)}
                    >
                      #{memoShoe.ranking[category]} of {shoeCount}
                    </Link>
                    )
                  </span>
                )}
              </div>
            )}
            <div style={{ fontSize: 16 }}>
              User {Helper.capitalize(category)} Rating:&nbsp;
              <span
                style={{
                  fontWeight: "700",
                  fontSize: 18,
                  color: Helper.get_color(memoShoe["user_" + category], "text"),
                }}
              >
                {Helper.printRating(memoShoe["user_" + category])}
              </span>
              &nbsp;
              {memoShoe["user_" + category] !== "NA" && (
                <span style={{}}>
                  (ranks{" "}
                  <Link
                    style={{ fontWeight: "600" }}
                    to={Helper.getSortUrl("user_" + category)}
                  >
                    #{memoShoe.ranking["user_" + category]} of {shoeCount}
                  </Link>
                  )
                </span>
              )}
            </div>
          </div>
        )}
        <Form.Control
          style={{ maxWidth: 300, fontSize: 14, marginBottom: 15 }}
          onChange={(e) => {
            setOption(Number(e.target.value));
          }}
          value={option}
          as="select"
        >
          <option key={0} value={0}>
            {getHistoCount(histo(0))} Total Ratings
          </option>
          <option key={1} value={1}>
            {getHistoCount(histo(1))} Expert Ratings
          </option>
          <option key={2} value={2}>
            {getHistoCount(histo(2))} User Ratings
          </option>
        </Form.Control>
        <div style={{ marginBottom: 40 }}>
          {histo(option).map((row, index) => (
            <div key={index} style={{ display: "flex", marginBottom: 5 }}>
              <div style={{ flexBasis: 100, fontSize: 14 }}>{row.label}</div>
              <div style={{ flexBasis: 200 }}>
                <div
                  style={{
                    width: "100%",
                    height: 20,
                    backgroundColor: "rgb(239, 239, 239)",
                  }}
                >
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip style={{ fontSize: 14 }} id="tooltip">
                        {row.count + " ratings " + row.description}
                      </Tooltip>
                    }
                  >
                    <div
                      style={{
                        transition: "width 0.5s",
                        backgroundColor: "orange",
                        height: 20,
                        width:
                          (row.count / getHistoCount(histo(option))) * 100 +
                          "%",
                      }}
                    ></div>
                  </OverlayTrigger>
                </div>
              </div>
              <div style={{ textAlign: "right", flexBasis: 36, fontSize: 14 }}>
                {row.count}
              </div>
            </div>
          ))}
        </div>



        {category !== "durability" && reviews[category].length === 0 && (
          <div style={{ marginBottom: 20, fontSize: 16 }}>
            No expert reviews for this category so far.
          </div>
        )}

        {category !== "durability" &&
          reviews[category].map((review, index) => {
            return (
              <div key={review.review_id}>

                <QuoteBox
                  pickReview={() =>
                    props.setPickedReview({
                      ...review,
                      shoe_name: memoShoe.shoe_name,
                    })
                  }
                  showYoutube={showYoutube}
                  {...review}
                  category={category}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

const ExpertReviews = (props) => {
  let { memoShoe, showYoutube, shoeCount } = props;
  let expertSampling = memoShoe.reviews.map((x) => x.rating);
  let expertHisto = [
    {
      label: "10 - Amazing",
      count: expertSampling.filter((x) => Math.round(x) === 10).length,
      description: ">9.5",
    },
    {
      label: "9 - Great",
      count: expertSampling.filter((x) => Math.round(x) === 9).length,
      description: "8.5-9.5",
    },
    {
      label: "8 - Good",
      count: expertSampling.filter((x) => Math.round(x) === 8).length,
      description: "7.5-8.5",
    },
    {
      label: "7 - Average",
      count: expertSampling.filter((x) => Math.round(x) === 7).length,
      description: "6.5-7.5",
    },
    {
      label: "6 - Subpar",
      count: expertSampling.filter((x) => Math.round(x) === 6).length,
      description: "5.5-6.5",
    },
    {
      label: "5 - Poor",
      count: expertSampling.filter((x) => Math.round(x) === 5).length,
      description: "<5.5",
    },
  ];

  return (
    <>
      <div>
        <div style={{ marginBottom: 15, fontSize: 16 }}>
          Expert Rating:&nbsp;
          <span
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: Helper.get_color(memoShoe["rating"], "text"),
            }}
          >
            {Helper.printRating(memoShoe["rating"])}
          </span>
          &nbsp;
          <span style={{}}>
            (ranks{" "}
            <Link
              style={{ fontWeight: "600" }}
              to={Helper.getSortUrl("rating")}
            >
              #{memoShoe.ranking["rating"]} of {shoeCount}
            </Link>
            )
          </span>
        </div>

        <div style={{ marginBottom: 40 }}>
          {expertHisto.map((row, index) => (
            <div key={index} style={{ display: "flex", marginBottom: 5 }}>
              <div style={{ flexBasis: 100, fontSize: 14 }}>{row.label}</div>
              <div style={{ flexBasis: 200 }}>
                <div
                  style={{
                    width: "100%",
                    height: 20,
                    backgroundColor: "rgb(239, 239, 239)",
                  }}
                >
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip style={{ fontSize: 14 }} id="tooltip">
                        {row.count + " ratings " + row.description}
                      </Tooltip>
                    }
                  >
                    <div
                      style={{
                        backgroundColor: "orange",
                        height: 20,
                        width: (row.count / expertSampling.length) * 100 + "%",
                      }}
                    ></div>
                  </OverlayTrigger>
                </div>
              </div>
              <div style={{ textAlign: "right", flexBasis: 36, fontSize: 14 }}>
                {row.count}
              </div>
            </div>
          ))}
        </div>


        {memoShoe.reviews.map((review, index) => {
          return (
            <div key={review.review_id}>

              <QuoteBox
                pickReview={() =>
                  props.setPickedReview({
                    ...review,
                    shoe_name: memoShoe.shoe_name,
                  })
                }
                showYoutube={showYoutube}
                {...review}
                category={"overall"}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

const UserReviews = (props) => {
  let { memoShoe, shoeCount } = props;
  let userHisto = memoShoe.user_histo.rating;
  const navigate = useNavigate();
  return (
    <>
      <div style={{ marginBottom: 15, fontSize: 16 }}>
        User Rating:&nbsp;
        <span
          style={{
            fontSize: 18,
            fontWeight: "700",
            color: Helper.get_color(memoShoe["user_rating"], "text"),
          }}
        >
          {Helper.printRating(memoShoe["user_rating"])}
        </span>
        &nbsp;
        <span style={{}}>
          (ranks{" "}
          <Link
            style={{ fontWeight: "600" }}
            to={Helper.getSortUrl("user_rating")}
          >
            #{memoShoe.ranking["user_rating"]} of {shoeCount}
          </Link>
          )
        </span>
      </div>
      <div style={{ marginBottom: 20 }}>
        {userHisto.map((row, index) => (
          <div key={index} style={{ display: "flex", marginBottom: 5 }}>
            <div style={{ flexBasis: 100, fontSize: 14 }}>{row.label}</div>
            <div style={{ flexBasis: 200 }}>
              <div
                style={{
                  width: "100%",
                  height: 20,
                  backgroundColor: "rgb(239, 239, 239)",
                }}
              >
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip style={{ fontSize: 14 }} id="tooltip">
                      {row.count + " ratings " + row.description}
                    </Tooltip>
                  }
                >
                  <div
                    style={{
                      backgroundColor: "orange",
                      height: 20,
                      width:
                        (row.count / memoShoe.user_review_amount) * 100 + "%",
                    }}
                  ></div>
                </OverlayTrigger>
              </div>
            </div>
            <div style={{ textAlign: "right", flexBasis: 36, fontSize: 14 }}>
              {row.count}
            </div>
          </div>
        ))}
      </div>

      <Button
        style={{ marginBottom: 40, fontSize: 14 }}
        size="sm"
        onClick={() =>
          navigate("/shoe-reviews/rating?shoe_id=" + memoShoe.shoe_id)
        }
        variant="outline-dark"
      >
        Add Your Review
      </Button>


      {memoShoe.user_reviews.slice(0, 5).map((review, index) => {
        return (
          <div key={review.user_review_id}>

            <QuoteBox
              pickReview={() =>
                props.setPickedUserReview({
                  ...review,
                  shoe_name: memoShoe.shoe_name,
                })
              }
              openStylePopup={props.openStylePopup}
              by="user"
              {...review}
              category={"overall"}
            />
          </div>
        );
      })}
    </>
  );
};

function Shoe(props) {
  const [sortingStyles, setSortingStyles] = useState(false);
  const [activeStyleId, setActiveStyleId] = useState(0);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [videoID, setVideoID] = useState("");
  const [swiper, setSwiper] = useState(null);
  const [currentFooterPosition, setCurrentFooterPosition] = useState(0);
  const [linkId, setLinkId] = useState("");
  const [currentPosition, setCurrentPosition] = useState(0);
  const [showingMoreShops, setShowingMoreShops] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [pickedReview, setPickedReview] = useState(false);
  const [pickedUserReview, setPickedUserReview] = useState(false);
  const [ratingSwitch, setRatingSwitch] = useState("EXPERT");

  const showSSRVersion = useShowSSRVersion();

  const navigate = useNavigate();

  const openStylePopup = (style_id) => {
    setShowStyleModal(true);
    setLinkId("user_review");
    setActiveStyleId(style_id);
  };

  let shoe_name = props.url;

  let memoShoe = useShoe(
    shoe_name.toLowerCase(),
    props.currentShoe,
    props.data,
    props.actions.shoeData
  );

  let location = useLocation();

  useEffect(() => {
    let parsed = queryString.parse(location.search);
    if (Object.prototype.hasOwnProperty.call(parsed, "style_id")) {
      setShowStyleModal(true);
      setLinkId("swiper");
      setActiveStyleId(parsed.style_id);
    }
  }, [location]);

  const showYoutube = (videoID) => {
    setVideoID(videoID);
    setShowModal(true);
  };

  const sortStyles = (styles, sortBy) => {
    let returnStyles = produce(styles, (draftStyles) => {
      if (sortBy == 0) {
        draftStyles.sort((a, b) => Helper.sortStylesByPrice(a, b));
      } else if (sortBy == 1) {
        draftStyles.sort((a, b) => Helper.sortStylesByLikes(a, b));
      }
    });
    return returnStyles;
  };

  if (!memoShoe) {
    return (
      <div>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  let calcSortStylesBy = showSSRVersion ? "0" : props.sortStylesBy;

  let shareUrl =
    memoShoe &&
    "https://www.thehoopsgeek.com/shoe-reviews/" +
    memoShoe.url.toLowerCase() +
    "/";
  let pinterestMedia =
    memoShoe && memoShoe.pictures.hasOwnProperty(1)
      ? "https://www.thehoopsgeek.com" + memoShoe.pictures[1].url
      : "";
  let sortedStyles =
    memoShoe &&
    memoShoe.hasOwnProperty("styles") &&
    sortStyles(memoShoe.styles, calcSortStylesBy);
  let filteredStyles =
    sortedStyles && sortedStyles.filter((x) => x.colorways.length > 0);
  let pictureArray =
    memoShoe &&
    Object.values(memoShoe.pictures).sort((a, b) => a.type - b.type);
  let categories = [
    "traction",
    "cushion",
    "materials",
    "support",
    "fit",
    "outdoor",
    "width",
    "size",
  ];
  let reviews = {};

  let activeStyle = sortedStyles
    ? {
      ...sortedStyles.filter((style) => style.style_id === activeStyleId)[0],
      shoe_name: memoShoe.shoe_name,
      shoe_url: memoShoe.url,
    }
    : false;

  if (memoShoe.hasOwnProperty("reviews")) {
    categories.forEach((category) => {
      reviews[category] = memoShoe.reviews.filter((x) => x[category] !== "-1");
    });
  }

  const colorways_by_shop = Helper.sort_colorways_by_shop(memoShoe.colorways);
  const resells_by_shop = Helper.sort_colorways_by_shop(memoShoe.resells);

  let colorways_and_resells_by_shop = [
    ...colorways_by_shop,
    ...resells_by_shop,
  ];

  if (memoShoe !== null) {
    let release_date = Helper.getDateFromMysql(memoShoe.release_date);

    return (
      <div style={{ paddingBottom: 100, boxSizing: "border-box!important" }}>
        <ExpertReviewPopup
          hide={() => setPickedReview(false)}
          review={pickedReview}
        />
        <UserReviewPopup
          hide={() => setPickedUserReview(false)}
          review={pickedUserReview}
        />

        {pictureArray && showGallery && (
          <Gallery
            activeSlide={activeSlide}
            setActiveSlide={setActiveSlide}
            showGallery={showGallery}
            closeGallery={() => {
              setShowGallery(false);
            }}
            pictures={pictureArray}
          />
        )}
        {getHelmet(memoShoe)}
        {activeStyle && showStyleModal && (
          <Modal
            dialogClassName="thg_style_modal"
            onHide={() => {
              setShowStyleModal(false);
            }}
            show={showStyleModal}
          >
            <StyleModal
              linkId={linkId}
              hideModal={() => {
                setShowStyleModal(false);
                if (location.search !== "") {
                  navigate("/shoe-reviews/" + memoShoe.url + "/");
                }
              }}
              activeStyle={activeStyle}
            />
          </Modal>
        )}

        <Modal
          size="lg"
          onHide={() => {
            setShowModal(false);
          }}
          show={showModal}
        >
          <YoutubeModal videoID={videoID} />
        </Modal>

        {/* Breadcrumbs */}
        <div>
          <p style={{ marginBottom: 10, marginTop: 10 }} id="breadcrumbs_react">
            <span>
              <Link
                onClick={() => {
                  props.actions.switchValue(0);
                }}
                to={"/shoe-reviews/"}
              >
                Basketball Shoes
              </Link>{" "}
              &nbsp;/&nbsp;
              <span className="breadcrumb_last">{memoShoe.shoe_name}</span>
            </span>
          </p>
        </div>

        {/* Title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <h1 className="thg_h1_size" style={{ marginBottom: 0 }}>
            {memoShoe.shoe_name}
          </h1>

          {(showSSRVersion ? [] : props.favs).filter(
            (x) => x === memoShoe.shoe_id
          ).length > 0 && (
              <i
                title="Remove from Favourites"
                onClick={(x) => {
                  props.removeFav(memoShoe);
                }}
                className="fas fa-star"
                style={{
                  fontSize: 30,
                  marginLeft: 5,
                  color: "orange",
                  cursor: "pointer",
                }}
              />
            )}
        </div>

        {/* Release info and favorites */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            fontSize: 14,
            marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 14 }}>
            Released {release_date.getMonth() + 1}/{release_date.getFullYear()},{" "}
            {memoShoe.style}
            {memoShoe.player !== "No Signature" && (
              <>
                ,{" "}
                <Link
                  style={{ fontWeight: 600 }}
                  to={Helper.getPlayerUrl(memoShoe.player, props.urlMapping)}
                >
                  {memoShoe.player}
                </Link>
              </>
            )}
            ,{"\u00A0"}
          </div>
          {(showSSRVersion ? [] : props.favs).filter(
            (x) => x === memoShoe.shoe_id
          ).length === 0 ? (
            <div
              onClick={(x) => {
                props.actions.addFav(memoShoe);
              }}
              style={{ alignItems: "center", display: "flex" }}
            >
              <div style={{ marginRight: 5, cursor: "pointer" }}>
                Add to Favourites
              </div>
              <i
                style={{
                  fontSize: 16,
                  lineHeight: "22.4px",
                  color: "orange",
                  cursor: "pointer",
                }}
                className="far fa-star"
              ></i>
            </div>
          ) : (
            <div
              onClick={(x) => {
                props.actions.removeFav(memoShoe);
              }}
              style={{ alignItems: "center", display: "flex" }}
            >
              <div style={{ marginRight: 5, cursor: "pointer" }}>
                Remove from Favourites
              </div>
              <i
                style={{
                  fontSize: 16,
                  lineHeight: "22.4px",
                  cursor: "pointer",
                  color: "#666666",
                }}
                className="far fa-trash-alt"
              ></i>
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* ROW 1: Images (left) + Price Comparison (right) */}
        {/* ============================================ */}
        <div
          css={{
            "@media (min-width:992px)": {
              display: "flex",
              gap: 40,
              alignItems: "flex-start",
            },
            marginBottom: 40,
            overflow: "hidden",
          }}
        >
          {/* Left: Image Swiper */}
          <div
            css={{
              "@media (max-width:991px)": {
                margin: "0 auto 20px",
                maxWidth: 600,
                width: "100%",
              },
              "@media (min-width:992px)": {
                backgroundColor: "#f8f8f8",
                maxWidth: "50%",
                flex: "1 1 auto",
              },
              textAlign: "center",
              borderRadius: 12,
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            <div
              className="hover-swiper"
              style={{
                flex: 1,
                alignItems: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Swiper
                onActiveIndexChange={(x) => {
                  setActiveSlide(x.activeIndex);
                }}
                className="gallery"
                lazy={{ loadPrevNext: true }}
                key={memoShoe.shoe_id}
                style={{ backgroundColor: "#f9f9f9", borderRadius: 12 }}
                watchOverflow={true}
                onSwiper={(x) => {
                  setSwiper(x);
                }}
                navigation={{
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }}
                pagination={{ clickable: false }}
              >
                {pictureArray.map((picture, index) => {
                  return (
                    <SwiperSlide key={picture.master}>
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-end",
                        }}
                      >
                        <div style={{ maxWidth: 600, flex: 1 }}>
                          <div
                            css={{
                              "@media (max-width:991px)": {
                                paddingBottom: "64%",
                                position: "relative",
                              },
                              "@media (min-width:992px)": {
                                paddingBottom: "68%",
                                position: "relative",
                              },
                            }}
                          >
                            <div
                              css={{
                                position: "absolute",
                                bottom: 50,
                                top: 20,
                                left: 10,
                                right: 10,
                              }}
                            >
                              <picture>
                                <source
                                  type="image/webp"
                                  data-srcset={Helper.create_source_set(
                                    { master: picture.master, thumbnails: "1" },
                                    undefined,
                                    "webp"
                                  )}
                                  sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                />
                                <source
                                  type="image/png"
                                  data-srcset={Helper.create_source_set({
                                    master: picture.master,
                                    thumbnails: "1",
                                  })}
                                  sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                />
                                {index === 0 ? (
                                  <picture>
                                    <source
                                      type="image/webp"
                                      srcSet={Helper.create_source_set(
                                        {
                                          master: picture.master,
                                          thumbnails: "1",
                                        },
                                        undefined,
                                        "webp"
                                      )}
                                      sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                    />
                                    <source
                                      type="image/png"
                                      srcSet={Helper.create_source_set({
                                        master: picture.master,
                                        thumbnails: "1",
                                      })}
                                      sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                    />
                                    <img
                                      onClick={() => {
                                        setShowGallery(true);
                                      }}
                                      width={picture.width}
                                      height={picture.height}
                                      css={{
                                        cursor: "zoom-in",
                                        maxHeight: 400,
                                        maxWidth: 550,
                                        transform:
                                          picture.mirror == "1" && "scaleX(-1)",
                                        objectPosition: "bottom center",
                                        objectFit: "contain",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                      src={Helper.add_options_img(
                                        picture.master,
                                        585
                                      )}
                                      sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                      alt={memoShoe.shoe_name}
                                      srcSet={Helper.create_source_set({
                                        master: picture.master,
                                        thumbnails: "1",
                                      })}
                                    />
                                  </picture>
                                ) : (
                                  <picture>
                                    <source
                                      type="image/webp"
                                      data-srcset={Helper.create_source_set(
                                        {
                                          master: picture.master,
                                          thumbnails: "1",
                                        },
                                        undefined,
                                        "webp"
                                      )}
                                      sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                    />
                                    <source
                                      type="image/png"
                                      data-srcset={Helper.create_source_set({
                                        master: picture.master,
                                        thumbnails: "1",
                                      })}
                                      sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                    />
                                    <img
                                      onClick={() => {
                                        setShowGallery(true);
                                      }}
                                      width={picture.width}
                                      height={picture.height}
                                      css={{
                                        cursor: "zoom-in",
                                        maxHeight: 400,
                                        maxWidth: 550,
                                        transform:
                                          picture.mirror == "1" && "scaleX(-1)",
                                        objectPosition: "bottom center",
                                        objectFit: "contain",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                      data-src={Helper.add_options_img(
                                        picture.master,
                                        585
                                      )}
                                      sizes="(max-width:600px) calc(100vw-35px), (max-width:992px) 585px,(max-width: 1200px) 50vw, 585px"
                                      className="swiper-lazy"
                                      alt={memoShoe.shoe_name}
                                      data-srcset={Helper.create_source_set({
                                        master: picture.master,
                                        thumbnails: "1",
                                      })}
                                    />
                                  </picture>
                                )}
                              </picture>
                            </div>
                          </div>
                        </div>
                      </div>
                      {index !== 0 && (
                        <div className="swiper-lazy-preloader"></div>
                      )}
                    </SwiperSlide>
                  );
                })}

                <div className="swiper-button-next next-button-hover" />
                <div className="swiper-button-prev prev-button-hover" />
              </Swiper>
            </div>
          </div>

          {/* Right: Price Comparison */}
          <div
            css={{
              "@media (min-width:992px)": {
                maxWidth: 400,
                flexShrink: 0,
              },
            }}
          >
            <SectionHeader>Price Comparison</SectionHeader>
            {!memoShoe.hasOwnProperty("colorways") ? (
              <div
                style={{ display: "flex", alignItems: "center", marginTop: 20 }}
              >
                <Spinner animation="border" variant="primary" />
                <span style={{ marginLeft: 10 }}>...loading shops</span>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 15, fontSize: 16 }}>
                  Available at {colorways_and_resells_by_shop.length}{" "}
                  {colorways_and_resells_by_shop.length === 1
                    ? "shop"
                    : "shops"}{" "}
                  | Official Retail Price: ${memoShoe.price}
                </div>

                {colorways_and_resells_by_shop
                  .slice(0, showingMoreShops ? 100 : showShopsAmount)
                  .map((colorways, index) => {
                    return (
                      <DealRow
                        key={index}
                        style={{ borderTopWidth: index === 0 ? 0 : 1 }}
                        trackingId={"price_comp"}
                        colorway={colorways.colorways[0]}
                      />
                    );
                  })}

                {showingMoreShops &&
                  colorways_and_resells_by_shop.length > showShopsAmount && (
                    <div
                      onClick={() => {
                        setShowingMoreShops(false);
                      }}
                      style={{
                        fontSize: 16,
                        cursor: "pointer",
                        color: "var(--primary-color-700)",
                        fontWeight: 600,
                        marginTop: 10,
                      }}
                    >
                      <i className="fas fa-arrow-up" /> See less shops
                    </div>
                  )}
                {colorways_and_resells_by_shop.length === 0 && (
                  <div>No shops found</div>
                )}

                {!showingMoreShops &&
                  colorways_and_resells_by_shop.length > showShopsAmount && (
                    <div
                      onClick={() => {
                        setShowingMoreShops(true);
                      }}
                      style={{
                        fontSize: 16,
                        cursor: "pointer",
                        color: "var(--primary-color-600)",
                        fontWeight: "bold",
                        marginTop: 10,
                      }}
                    >
                      <i className="fas fa-arrow-down" /> See all{" "}
                      {colorways_and_resells_by_shop.length} shops
                    </div>
                  )}
              </>
            )}
          </div>
        </div>

        {/* ============================================ */}
        {/* ROW 2: Overall Ratings + Short Summary + Best For */}
        {/* ============================================ */}
        <div
          css={{
            marginBottom: 40,
            display: "grid",
            gap: 40,
            "@media (min-width:992px)": {
              gridTemplateColumns: "auto 1fr",
            },
          }}
        >
          {/* Ratings Box */}
          <div
            css={{
              backgroundColor: "#f8f8f8",
              borderRadius: 16,
              padding: "24px 40px",
              "@media (min-width:1200px)": {
                padding: "24px 80px",
              },
            }}
          >
            <SectionHeader style={{ marginBottom: 20 }}>
              Performance Ratings
            </SectionHeader>
            <div
              css={{
                display: "flex",
                gap: 30,
                justifyContent: "center",
                alignItems: "flex-end",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <RatingCircle
                  containerStyle={{
                    width: 120,
                    height: 120,
                    margin: "0 auto",
                  }}
                  of={props.data.length}
                  {...memoShoe}
                  textStyle={{ fontSize: 48, lineHeight: "48px" }}
                />
                <div style={{ fontWeight: 600, fontSize: 14, marginTop: 8 }}>
                  EXPERT RATING
                </div>
                <div
                  onClick={() =>
                    document.getElementById("overall").scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                      inline: "nearest",
                    })
                  }
                  style={{
                    color: "var(--primary-color-600)",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {memoShoe.review_amount} reviews
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <RatingCircle
                  containerStyle={{
                    width: 80,
                    height: 80,
                    margin: "0 auto",
                  }}
                  of={props.data.length}
                  {...memoShoe}
                  rating={memoShoe.user_rating}
                  textStyle={{ fontSize: 32, lineHeight: "32px" }}
                />
                <div style={{ fontWeight: 600, fontSize: 13, marginTop: 8 }}>
                  USER RATING
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                >
                  <span
                    onClick={() =>
                      document.getElementById("user_overall").scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest",
                      })
                    }
                    style={{
                      color: "var(--primary-color-600)",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {memoShoe.user_review_amount} ratings
                  </span>
                  <i
                    onClick={() =>
                      navigate(
                        "/shoe-reviews/rating?shoe_id=" + memoShoe.shoe_id
                      )
                    }
                    className="fas fa-plus-circle"
                    style={{
                      color: "var(--primary-color-600)",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                    title="Add your rating"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary Box */}
          <div
            css={{
              backgroundColor: "#f8f8f8",
              borderRadius: 16,
              padding: "24px",
              color: "#333",
            }}
          >
            <SectionHeader style={{ marginBottom: 20 }}>
              Performance Summary
            </SectionHeader>

            {memoShoe.short_summary !== "" && (
              <div
                style={{
                  fontSize: 16,
                  marginBottom: 24,
                  lineHeight: 1.6,
                }}
              >
                <i
                  className="fas fa-quote-left"
                  style={{ marginRight: 8, opacity: 0.5 }}
                />
                {memoShoe.short_summary}
              </div>
            )}

            {/* Pros and Cons */}
            {(memoShoe.pros.length > 0 || memoShoe.cons.length > 0) && (
              <div
                css={{
                  display: "grid",
                  gap: 16,
                  marginTop: 8,
                  "@media (min-width:768px)": {
                    gridTemplateColumns: "1fr 1fr",
                  },
                }}
              >
                {memoShoe.pros.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        marginBottom: 8,
                        fontWeight: 700,
                        color: "#48BB78",
                      }}
                    >
                      Pros
                    </div>
                    {memoShoe.pros.slice(0, 4).map((pro) => (
                      <div
                        key={pro.pro_id}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: 6,
                        }}
                      >
                        <i
                          style={{
                            fontSize: 14,
                            lineHeight: "24px",
                            color: "#48BB78",
                            marginRight: 8,
                            flexShrink: 0,
                          }}
                          className="fas fa-check"
                        />
                        <div
                          style={{
                            fontSize: 16,
                            lineHeight: 1.5,
                          }}
                        >
                          {pro.text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {memoShoe.cons.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        marginBottom: 8,
                        fontWeight: 700,
                        color: "#E53E3E",
                      }}
                    >
                      Cons
                    </div>
                    {memoShoe.cons.slice(0, 4).map((con) => (
                      <div
                        key={con.con_id}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: 6,
                        }}
                      >
                        <i
                          style={{
                            fontSize: 14,
                            lineHeight: "24px",
                            color: "#E53E3E",
                            marginRight: 8,
                            flexShrink: 0,
                          }}
                          className="fas fa-times"
                        />
                        <div
                          style={{
                            fontSize: 16,
                            lineHeight: 1.5,
                          }}
                        >
                          {con.text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {memoShoe.best_text !== "" && (
              <div style={{ marginTop: 24, fontSize: 16 }}>
                <span style={{}}>Best Suitable For:</span> {memoShoe.best_text}
              </div>
            )}
          </div>
        </div>




        {/* ============================================ */}
        {/* ROW 3: Category Ratings as Circles + Size/Width Scales */}
        {/* ============================================ */}
        <div style={{ marginBottom: 40, marginTop: 40 }}>
          {/* Header row with title and toggle */}
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 20,
              "@media (min-width:768px)": {
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              },
            }}
          >
            <SectionHeader style={{ margin: 0 }}>
              Category Performance Ratings
            </SectionHeader>

            {/* Expert/User Toggle */}
            <div
              style={{
                display: "flex",
                width: 300,
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 12,
                overflow: "hidden",
                border: "1px solid #ccc",
              }}
            >
              <div
                onClick={() => setRatingSwitch("EXPERT")}
                style={
                  ratingSwitch === "EXPERT"
                    ? activeSwitchStyle
                    : inactiveSwitchStyle
                }
              >
                <i
                  style={{
                    marginRight: 5,
                    visibility:
                      ratingSwitch === "EXPERT" ? "visible" : "hidden ",
                  }}
                  className="fas fa-check"
                />
                Expert Ratings ({memoShoe.review_amount})
              </div>
              <div
                onClick={() => setRatingSwitch("USER")}
                style={
                  ratingSwitch === "EXPERT"
                    ? inactiveSwitchStyle
                    : activeSwitchStyle
                }
              >
                <i
                  style={{
                    marginRight: 5,
                    visibility:
                      ratingSwitch === "EXPERT" ? "hidden" : "visible ",
                  }}
                  className="fas fa-check"
                />
                User Ratings ({memoShoe.user_review_amount})
              </div>
            </div>
          </div>

          {/* Category Rating Cards + Sizing + Width */}
          <div
            css={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 16,
            }}
          >
            {(ratingSwitch === "EXPERT"
              ? [
                "traction",
                "cushion",
                "materials",
                "support",
                "fit",
                "outdoor",
              ]
              : [
                "traction",
                "cushion",
                "materials",
                "support",
                "fit",
                "outdoor",
                "durability",
              ]
            ).map((category) => (
              <CategoryRatingCard
                key={category}
                category={category}
                rating={
                  ratingSwitch === "EXPERT"
                    ? memoShoe[category]
                    : memoShoe["user_" + category]
                }
                userRating={memoShoe["user_" + category]}
                description={categoryDescriptions[category]}
                summary={
                  ratingSwitch === "EXPERT"
                    ? memoShoe[category + "_summary"]
                    : ""
                }
                onClick={() => {
                  document
                    .getElementById(Helper.capitalize(category))
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              />
            ))}

            {/* Sizing Card */}
            <div
              onClick={() => {
                document
                  .getElementById("Size")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              css={{
                padding: 16,
                backgroundColor: "#fafafa",
                borderRadius: 12,
                textAlign: "center",
                cursor: "pointer",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
            >
              <div
                style={{
                  marginBottom: 10,
                  height: 85,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isNaN(memoShoe.size) ? (
                  <div style={{ color: "#666", fontSize: 12 }}>
                    No data available
                  </div>
                ) : (
                  <SizeScale
                    shoe_name={memoShoe.shoe_name}
                    percent={memoShoe.size}
                    type="size"
                  />
                )}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  marginBottom: 8,
                  color: "#2d3748",
                  textTransform: "uppercase",
                }}
              >
                Sizing
              </div>
              {ratingSwitch === "EXPERT" && memoShoe.sizing_summary && (
                <div
                  style={{
                    fontSize: 14,
                    color: "#333",
                    lineHeight: 1.6,
                  }}
                >
                  {memoShoe.sizing_summary}
                </div>
              )}
            </div>

            {/* Width Card */}
            <div
              onClick={() => {
                document
                  .getElementById("Width")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              css={{
                padding: 16,
                backgroundColor: "#fafafa",
                borderRadius: 12,
                textAlign: "center",
                cursor: "pointer",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
            >
              <div
                style={{
                  marginBottom: 10,
                  height: 85,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isNaN(memoShoe.width) ? (
                  <div style={{ color: "#666", fontSize: 12 }}>
                    No data available
                  </div>
                ) : (
                  <SizeScale
                    shoe_name={memoShoe.shoe_name}
                    percent={memoShoe.width}
                    type="width"
                  />
                )}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  marginBottom: 8,
                  color: "#2d3748",
                  textTransform: "uppercase",
                }}
              >
                Width
              </div>
              {ratingSwitch === "EXPERT" && memoShoe.width_summary && (
                <div
                  style={{
                    fontSize: 14,
                    color: "#333",
                    lineHeight: 1.6,
                  }}
                >
                  {memoShoe.width_summary}
                </div>
              )}
            </div>
          </div>
        </div>


        {/* ============================================ */}
        {/* ROW 4: Comparison Widget */}
        {/* ============================================ */}
        <SmallHeader>Comparison to Similar Shoes:</SmallHeader>
        <SimilarComparison shoe={memoShoe} overview={props.overview} />



        {/* ============================================ */}
        {/* Colorways Slider */}
        {/* ============================================ */}
        <div className="hover-swiper" style={{ marginTop: 60 }}>
          <div className="d-md-flex" style={{ marginBottom: 10 }}>
            <div className="mb-md-2">
              <div>
                <Link
                  to={
                    "/shoe-reviews/" +
                    memoShoe.url.toLowerCase() +
                    "-colorways/"
                  }
                  id="colorways"
                  style={{
                    color: "#2d3748",
                    fontWeight: 600,
                    margin: 0,
                    fontSize: 20,
                  }}
                >
                  Colorways of {memoShoe.shoe_name}:
                </Link>
              </div>
              <Link
                style={{ fontSize: 16, fontWeight: "bold" }}
                to={
                  "/shoe-reviews/" + memoShoe.url.toLowerCase() + "-colorways/"
                }
              >
                See all {sortedStyles.length} colorways
              </Link>
            </div>

            <div className="d-flex justify-content-md-end">
              <Form.Control
                css={{
                  "@media (min-width:768px)": {
                    marginLeft: 20,
                    marginTop: 0,
                    fontSize: 16,
                  },
                  marginTop: 15,
                  width: "auto",
                }}
                value={calcSortStylesBy}
                onChange={(e) => {
                  setSortingStyles(true);
                  setTimeout(() => {
                    setSortingStyles(false);
                  }, 500);
                  props.actions.setSortStylesBy(e.target.value);
                }}
                as="select"
              >
                <option value={0}>sorted by price</option>
                <option value={1}>sorted by likes</option>
              </Form.Control>
            </div>
          </div>
          <div
            style={{ position: "relative", opacity: sortingStyles ? 0.5 : 1 }}
          >
            {sortedStyles && sortedStyles.length > 0 ? (
              <Swiper
                navigation={{
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }}
                style={{ margin: -5, padding: 5 }}
                slidesPerView="auto"
                spaceBetween={15}
              >
                {sortedStyles.map((style, index) => {
                  return (
                    <SwiperSlide key={style.style_id} style={{ width: 200 }}>
                      <StyleSliderItem
                        textContainerStyle={{ padding: 5 }}
                        showCounter
                        imageProps={{ padding: "10px 10px 20px" }}
                        styleItem={style}
                        swiperWidth={200}
                        imageHeight={200}
                        onClick={() => {
                          setShowStyleModal(true);
                          setActiveStyleId(style.style_id);
                          setLinkId("swiper");
                        }}
                      />
                    </SwiperSlide>
                  );
                })}
                <div
                  style={{ top: "10%" }}
                  className="swiper-button-next next-button-hover"
                />
                <div
                  style={{ top: "10%" }}
                  className="swiper-button-prev prev-button-hover"
                />
              </Swiper>
            ) : (
              <div style={{ fontSize: 14 }}>
                No colorways added for {memoShoe.shoe_name}
              </div>
            )}

            {sortingStyles && (
              <div
                style={{ position: "absolute", zIndex: 1, top: 50, left: 20 }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
          </div>
        </div>

        <Waypoint
          onPositionChange={(x) => setCurrentPosition(x.currentPosition)}
        />

        {/* ============================================ */}
        {/* Review Section */}
        {/* ============================================ */}
        <Row style={{ marginTop: 40 }}>
          <Col lg={8}>

            {memoShoe.hasOwnProperty("reviews") ? (
              <div style={{ marginBottom: 40 }}>
                <h3
                  className="thg_h3_size"
                  id="overall"
                  style={{ marginBottom: 10, marginTop: 100 }}
                >
                  Expert Reviews of the {memoShoe.shoe_name}:
                </h3>

                {memoShoe.review_amount > 0 ? (
                  <ExpertReviews
                    setPickedReview={setPickedReview}
                    memoShoe={memoShoe}
                    showYoutube={showYoutube}
                    shoeCount={props.overview.length}
                  />
                ) : (
                  <div style={{ fontSize: 16 }}>
                    No expert opinions found so far
                  </div>
                )}
                {!props.visitedQuiz && <QuizAdd />}

                <div>
                  <h3
                    className="thg_h3_size"
                    id="user_overall"
                    style={{ marginBottom: 10, marginTop: 100 }}
                  >
                    User Reviews of the {memoShoe.shoe_name}:
                  </h3>
                  {memoShoe.user_review_amount > 0 ? (
                    <UserReviews
                      setPickedUserReview={setPickedUserReview}
                      openStylePopup={openStylePopup}
                      memoShoe={memoShoe}
                      shoeCount={props.overview.length}
                    />
                  ) : (
                    <>
                      <div style={{ fontSize: 16, marginBottom: 10 }}>
                        No user reviews so far
                      </div>
                      <Button
                        style={{ marginBottom: 40, fontSize: 14 }}
                        size="sm"
                        onClick={() =>
                          navigate(
                            "/shoe-reviews/rating?shoe_id=" + memoShoe.shoe_id
                          )
                        }
                        variant="outline-dark"
                      >
                        Add Your Review
                      </Button>
                    </>
                  )}
                  {memoShoe.user_review_amount > 5 && (
                    <div style={{ marginTop: -20, marginBottom: 30 }}>
                      <Link
                        to={
                          "/shoe-reviews/user-reviews?shoe_id=" +
                          memoShoe.shoe_id
                        }
                        style={{
                          fontSize: 18,
                          lineHeight: 1.2,
                          color: "black",
                          fontWeight: 600,
                        }}
                      >
                        See all {memoShoe.user_review_amount} reviews
                      </Link>
                    </div>
                  )}
                </div>

                {[
                  "traction",
                  "cushion",
                  "materials",
                  "support",
                  "fit",
                  "durability",
                  "outdoor",
                  "width",
                  "size",
                ].map((category) => (
                  <CategoryBlock
                    setPickedReview={setPickedReview}
                    key={category}
                    shoeCount={props.overview.length}
                    category={category}
                    memoShoe={memoShoe}
                    reviews={reviews}
                    showYoutube={showYoutube}
                  />
                ))}

                <div style={{}}>
                  <h3 style={{ marginBottom: 30, marginTop: 100 }}>
                    Share this article:
                  </h3>
                  <div style={{ flex: 1, display: "flex" }}>
                    <div style={{ cursor: "pointer" }}>
                      <FacebookShareButton
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Facebook");
                        }}
                        url={shareUrl}
                      >
                        <FacebookIcon size={50} />
                      </FacebookShareButton>
                    </div>
                    <div style={{ paddingLeft: 5, cursor: "pointer" }}>
                      <TwitterShareButton
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Twitter");
                        }}
                        title={
                          memoShoe.shoe_name +
                          ": Average Expert Rating " +
                          memoShoe.rating +
                          "/10"
                        }
                        url={shareUrl}
                      >
                        <TwitterIcon size={50} />
                      </TwitterShareButton>
                    </div>
                    <div style={{ paddingLeft: 5, cursor: "pointer" }}>
                      <PinterestShareButton
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Pinterest");
                        }}
                        media={pinterestMedia}
                        url={shareUrl}
                      >
                        <PinterestIcon size={50} />
                      </PinterestShareButton>
                    </div>
                    <div style={{ paddingLeft: 5, cursor: "pointer" }}>
                      <EmailShareButton
                        subject={memoShoe.shoe_name + " Performance Review"}
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Email");
                        }}
                        url={shareUrl}
                      >
                        <EmailIcon size={50} />
                      </EmailShareButton>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>Loading</div>
            )}
          </Col>

          <Col className="d-none d-lg-block" lg={{ span: 4 }}>
            <div
              css={{
                "::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
                visibility: currentPosition === "above" ? "visible" : "hidden",
                opacity: currentPosition === "above" ? 1 : 0,
                transition: "opacity 0.3s ease-in",
                position: "sticky",
                maxHeight: "100vh",
                overflow: "scroll",
                paddingBottom: 100,
                top: 10,
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ width: 300, paddingLeft: 10, paddingBottom: 30 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 10,
                      fontSize: 16,
                      marginTop: 20,
                    }}
                  >
                    Sharing is caring:
                  </div>
                  <div style={{ flex: 1, display: "flex", marginBottom: 20 }}>
                    <div style={{ cursor: "pointer" }}>
                      <FacebookShareButton
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Facebook");
                        }}
                        url={shareUrl}
                      >
                        <FacebookIcon size={50} />
                      </FacebookShareButton>
                    </div>
                    <div style={{ paddingLeft: 5, cursor: "pointer" }}>
                      <TwitterShareButton
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Twitter");
                        }}
                        title={
                          memoShoe.shoe_name +
                          ": Average Expert Rating " +
                          memoShoe.rating +
                          "/10"
                        }
                        url={shareUrl}
                      >
                        <TwitterIcon size={50} />
                      </TwitterShareButton>
                    </div>
                    <div style={{ paddingLeft: 5, cursor: "pointer" }}>
                      <PinterestShareButton
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Pinterest");
                        }}
                        media={pinterestMedia}
                        url={shareUrl}
                      >
                        <PinterestIcon size={50} />
                      </PinterestShareButton>
                    </div>
                    <div style={{ paddingLeft: 5, cursor: "pointer" }}>
                      <EmailShareButton
                        subject={memoShoe.shoe_name + " Performance Review"}
                        beforeOnClick={() => {
                          Helper.gaEvent("reviews", "share", "Email");
                        }}
                        url={shareUrl}
                      >
                        <EmailIcon size={50} />
                      </EmailShareButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Waypoint
          onPositionChange={(x) => {
            setCurrentFooterPosition(x.currentPosition);
          }}
        />
      </div>
    );
  } else {
    Helper.gaEvent("error", "shoe-reviews", "couldnt find shoe");
    return (
      <div style={{ marginTop: 20 }}>
        Couldn't find shoe. Go to <Link to={"/shoe-reviews/"}> all shoes </Link>
      </div>
    );
  }
}

export default connect(
  (state) => ({ ...state.data, ...state.persistData }),
  (dispatch) => ({
    actions: bindActionCreators(MetricActions, dispatch),
  })
)(Shoe);
